import { landmineNames } from '@mm/constants/landmine-names';
import { useClickOutside } from '@mm/hooks';
import { ArrowStatefulIcon } from '@mm/icons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import commonHandler from '@mm/state/errorHandlers/common-handler';
import { partial, unionBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ProRibbon from '../../../../../common/components/ProRibbon/ProRibbon';
import TextInput from '../../../../../common/components/TextInput';
import {
  LeadContactsFilterByAudience,
  LeadContactsFilterByAudienceCreateButton,
} from '../../../beta/modules/LeadsContacts/LeadContactsFilterByAudience/LeadContactsFilterByAudience';
import { useCreateNewAudience } from '../../../beta/modules/LeadsContacts/use-create-new-audience';
import { useFetchAudiences } from '../../../beta/modules/LeadsContacts/use-fetch-audiences';
import './AudienceSelector.pcss';
import classNames from 'classnames';

type Props = {
  selectedAudienceId?: number;
  fallbackId?: number; // audience id to select if couldn't fetch
  selectedAudience?: AudiencesModel;
  onAudienceSelected: (audienceId: number) => void;
  disabled?: boolean;
  parentAudiences?: AudiencesModel[];
  formatText?: (name?: string) => string;
  autoselect?: boolean;
  className?: string;
  onCancel?: () => void;
};

export const AudienceSelector = (props: Props) => {
  const {
    fallbackId = -1,
    selectedAudienceId,
    onAudienceSelected,
    disabled = false,
    parentAudiences,
    formatText,
    autoselect,
    className,
    onCancel,
  } = props;

  const audiences = useSelector(selectAudienceSorted);
  const mergedAudiences = unionBy(parentAudiences, audiences, 'id');

  const modalCRUDActions = useModalActions();

  const { fetch, fetchMore, isFetching } = useFetchAudiences({
    skipBusy: true,
  });

  const canSelectAudiences = useSelector(
    combinedFeatureFlagsSelector,
  ).campaignsAudiencesSelection;

  useEffect(() => {
    if (!selectedAudienceId && mergedAudiences[0]) {
      onAudienceSelected(mergedAudiences[0].id);
    }
  }, [JSON.stringify(mergedAudiences)]);

  const upgradeGuardPass = (audienceId: number) => {
    const selectedAudience = mergedAudiences.find(
      item => item.id === audienceId,
    );
    if (!canSelectAudiences && selectedAudience?.messengerAdAudience) {
      modalCRUDActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['SELECT-AUDIENCE'],
        feature: 'campaignsAudiencesSelection',
      });
      return false;
    }

    return true;
  };

  const { createAudienceAndOpenMenu } = useCreateNewAudience(onCancel);

  const selectedAudience = mergedAudiences.find(
    a => a.id === selectedAudienceId,
  );

  const [isOpen, setIsOpen] = useState<boolean>();

  const onClose = partial(setIsOpen, false);
  const toggle = partial(setIsOpen, !isOpen);

  const onSelect = (audienceId?: number) => {
    if (audienceId && upgradeGuardPass(audienceId)) {
      onClose();
      onAudienceSelected(audienceId);
    }
  };

  const ref = useClickOutside<HTMLDivElement>(onClose);

  useEffect(() => {
    if (isOpen) {
      fetch(1, 25);
    }
  }, [isOpen]);

  const audiencesCRUDActions = useAudiencesCRUDActions();
  const botId = useSelector(activeChatbotIdSelector);
  const dispatch: Dispatch<StoreState> = useDispatch();

  useEffect(() => {
    if (
      selectedAudienceId &&
      !mergedAudiences.find(audience => audience.id === selectedAudienceId)
    ) {
      audiencesCRUDActions
        .retrieveAudienceById(botId, selectedAudienceId, true)
        .catch(err => {
          if (err.status === 404) {
            onAudienceSelected(fallbackId);
          } else {
            commonHandler(dispatch)(err);
          }
        });
    }
  }, []);

  const formattedText = useMemo(() => {
    if (formatText) {
      return formatText(selectedAudience?.name);
    }

    if (selectedAudience?.name) {
      return `Selected: ${selectedAudience?.name}`;
    }

    return 'Select audience';
  }, [selectedAudience?.name, formatText]);

  return (
    <div className={classNames('audience-selector', className)} ref={ref}>
      <div className="audience-selector__input-container" onClick={toggle}>
        <ArrowStatefulIcon
          className="audience-selector__arrow"
          direction={isOpen ? 'up' : 'down'}
          onClick={toggle}
          size={14}
        />
        <TextInput
          className="audience-selector__input"
          disabled={disabled}
          value={formattedText}
          onChange={() => {}}
          onFocus={e => e.target.blur()}
        />
      </div>

      {isOpen && (
        <LeadContactsFilterByAudience
          audiences={mergedAudiences}
          withMenu="edit"
          className="audience-selector__dropdown"
          listClassName="audience-selector__dropdown-list"
          isFetching={isFetching}
          onScrollEnd={isFetching ? undefined : fetchMore}
          activeAudienceId={selectedAudienceId}
          onSelect={onSelect}
          renderAudienceChild={audience => {
            if (!audience.messengerAdAudience) {
              return null;
            }

            return (
              <ProRibbon
                className="audience-selector__pro-audience"
                ribbonType="unicorn"
              />
            );
          }}
          headingSlotChildren={
            <LeadContactsFilterByAudienceCreateButton
              onClick={async () => {
                const id = await createAudienceAndOpenMenu({
                  withRedirectOnClose: false,
                });
                if (id) {
                  onSelect(id);
                }
              }}
            />
          }
        />
      )}
    </div>
  );
};
